import type { DynamicFilter, DynamicSort, DynamicSearch } from "../types";

export interface QueryParams {
  filters?: DynamicFilter[];
  sorts?: DynamicSort[];
  searches?: DynamicSearch[];
}

export interface SerializedQuery {
  filters?: string;
  sorts?: string;
  searches?: string;
}

export function serializeFiltersToQuery(
  filters: DynamicFilter[]
): string | undefined {
  if (!filters || filters.length === 0) {
    return undefined;
  }

  const cleanFilters = filters.map((filter) => ({
    filterBy: filter.filterBy,
    type: filter.type,
    value: filter.value,
  }));

  return JSON.stringify(cleanFilters);
}

/**
 * Alias for serializeFiltersToQuery - both now use same format
 */
export function serializeFiltersForAPI(
  filters: DynamicFilter[]
): string | undefined {
  return serializeFiltersToQuery(filters);
}

export function serializeSortsToQuery(
  sorts: DynamicSort[]
): string | undefined {
  if (!sorts || sorts.length === 0) {
    return undefined;
  }

  const serializedSorts = sorts.map((sort) => ({
    sortBy: sort.nestedField ? `${sort.field}.${sort.nestedField}` : sort.field,
    order: sort.direction === 0 ? "asc" : "desc",
  }));

  return JSON.stringify(serializedSorts);
}

export function serializeSearchesToQuery(
  searches: DynamicSearch[]
): string | undefined {
  if (!searches || searches.length === 0) {
    return undefined;
  }

  // Only include searches with non-empty values
  const validSearches = searches.filter(
    (s) => s.value && s.value.trim() !== ""
  );
  if (validSearches.length === 0) {
    return undefined;
  }

  return JSON.stringify(validSearches);
}

export function serializeQueryParams(params: QueryParams): SerializedQuery {
  const result: SerializedQuery = {};

  if (params.filters) {
    result.filters = serializeFiltersToQuery(params.filters);
  }

  if (params.sorts) {
    result.sorts = serializeSortsToQuery(params.sorts);
  }

  if (params.searches) {
    result.searches = serializeSearchesToQuery(params.searches);
  }

  return result;
}

export function parseFiltersFromQuery(queryString: string): DynamicFilter[] {
  try {
    return JSON.parse(queryString);
  } catch {
    return [];
  }
}

export function parseSortsFromQuery(queryString: string): DynamicSort[] {
  try {
    const parsed = JSON.parse(queryString);
    // Convert from serialized format { sortBy, order } to DynamicSort format { field, direction }
    return parsed.map((item: any) => {
      // Handle both old format (already correct) and new serialized format
      if (item.field !== undefined && item.direction !== undefined) {
        return item; // Already in correct format
      }
      // Convert from serialized format
      const fieldParts = item.sortBy?.split(".") || [];
      return {
        field: fieldParts[0] || item.sortBy || "",
        direction: item.order === "desc" ? 1 : 0,
        nestedField:
          fieldParts.length > 1 ? fieldParts.slice(1).join(".") : undefined,
      };
    });
  } catch {
    return [];
  }
}

export function parseSearchesFromQuery(queryString: string): DynamicSearch[] {
  try {
    return JSON.parse(queryString);
  } catch {
    return [];
  }
}
